import { useMemo } from 'react';
import {
    Card,
    CardContent,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components';

import { BiasedWord } from '@/types/common';
import {
    BIAS_CATEGORIES,
    BIAS_HIGHLIGHT_COLORS,
    BIAS_REASONS,
} from '@/config/app';
import { cn } from '@/lib/utils';

interface HighlightedTextProps {
    text: string;
    biasedWords: BiasedWord[];
    biasFilter: string;
}

// Extracted as a separate utility function
function combineBiasedWordGroups(biasedWords: BiasedWord[]): BiasedWord[] {
    const combinedWords: BiasedWord[] = [];
    let currentWord = '';
    let currentMetadata: BiasedWord | null = null;

    for (const token of biasedWords) {
        const word = token.word;

        if (word.startsWith('##')) {
            currentWord += word.slice(2);
            if (currentMetadata) {
                currentMetadata.word = currentWord;
                currentMetadata.labels = Array.from(
                    new Set([...currentMetadata.labels, ...token.labels])
                );
                currentMetadata.reasons = Array.from(
                    new Set([...currentMetadata.reasons, ...token.reasons])
                );
            }
        } else {
            if (currentMetadata) combinedWords.push(currentMetadata);
            currentWord = word;
            currentMetadata = {
                ...token,
                word: currentWord,
                labels: [...token.labels],
                reasons: [...token.reasons],
            };
        }
    }

    if (currentMetadata) combinedWords.push(currentMetadata);
    return combinedWords;
}

// Extracted as a separate component
const BiasedWordSpan = ({
    word,
    category,
    index,
}: {
    word: string;
    category: string;
    index: number;
}) => (
    <TooltipProvider key={index}>
        <Tooltip>
            <TooltipTrigger asChild>
                <span
                    className={cn(
                        `px-1 rounded font-bold cursor-pointer`,
                        `${BIAS_HIGHLIGHT_COLORS[category]} `
                    )}
                >
                    {word}
                </span>
            </TooltipTrigger>
            <TooltipContent>
                <p className="text-xs font-bold text-gray-600">
                    Classified as {BIAS_CATEGORIES[category]} bias: the word "
                    {word}" enforces the {BIAS_REASONS[category]}
                </p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

// Extracted as a separate component
const BiasCard = ({
    children,
    category,
    percentage,
}: {
    children: React.ReactNode;
    category: string;
    percentage: number;
}) => (
    <Card>
        <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-2 sm:gap-0">
                <p className="w-full sm:w-2/3 text-sm sm:text-base">
                    {children}
                </p>
                <p className="flex justify-start sm:justify-end items-center w-full sm:w-1/3 text-sm sm:text-base">
                    {BIAS_CATEGORIES[category]} (<b>{percentage}%</b>)
                </p>
            </div>
        </CardContent>
    </Card>
);

export function HighlightedText({
    text,
    biasedWords,
    biasFilter,
}: HighlightedTextProps) {
    // Use useMemo to avoid unnecessary recalculations
    const { words, processedBiasedWords, categoryCounts } = useMemo(() => {
        const words = text.split(/\s+/);
        const processedBiasedWords = combineBiasedWordGroups(biasedWords);
        const categoryCounts: Record<string, number> = {};

        Object.keys(BIAS_CATEGORIES).forEach((category) => {
            categoryCounts[category] = processedBiasedWords.filter((word) =>
                word.labels.includes(category)
            ).length;
        });

        return { words, processedBiasedWords, categoryCounts };
    }, [text, biasedWords]);

    // Filter and sort categories
    const filteredCategories = useMemo(
        () =>
            Object.keys(BIAS_CATEGORIES)
                .filter(
                    (category) =>
                        biasFilter.includes('ALL') || biasFilter === category
                )
                .sort((a, b) => {
                    const countA = categoryCounts[a];
                    const countB = categoryCounts[b];

                    if (countA === 0 && countB > 0) return 1;
                    if (countA > 0 && countB === 0) return -1;
                    return countB - countA;
                }),
        [categoryCounts, biasFilter]
    );

    // Generate highlighted sections for each category
    const highlightedSections = useMemo(
        () =>
            filteredCategories
                .map((category, i) => {
                    const count = categoryCounts[category];
                    const percentage =
                        processedBiasedWords.length > 0
                            ? Math.round(
                                  (count / processedBiasedWords.length) * 100
                              )
                            : 0;

                    // Skip rendering categories with no matches
                    if (count === 0) return null;

                    const highlightedText = words.map((word, index) => {
                        const biasedWord = processedBiasedWords.find(
                            (bw) =>
                                bw.index === index &&
                                bw.labels.includes(category)
                        );

                        return biasedWord ? (
                            <BiasedWordSpan
                                word={word}
                                category={category}
                                index={index}
                                key={index}
                            />
                        ) : (
                            <span key={index}>{word} </span>
                        );
                    });

                    return (
                        <BiasCard
                            key={i}
                            category={category}
                            percentage={percentage}
                        >
                            {highlightedText}
                        </BiasCard>
                    );
                })
                .filter(Boolean),
        [filteredCategories, categoryCounts, processedBiasedWords, words]
    );

    return (
        <div
            className={cn('space-y-4 px-2 sm:px-0', {
                'overflow-y-scroll max-h-[250px] sm:max-h-[300px]':
                    biasFilter.includes('ALL'),
            })}
        >
            {highlightedSections.length > 0 ? (
                highlightedSections
            ) : (
                <p className="text-gray-500 text-center py-4 text-sm sm:text-base">
                    No bias detected in selected categories.
                </p>
            )}
        </div>
    );
}
